/**
 * Parse device model string into vendor and device name
 *
 * Device model is stored in format: "vendor_device" (e.g., "garmin_fenix_7", "form_swim goggles")
 *
 * @param deviceModel - Device model string from database
 * @returns Object with vendor and device, or null if no device model
 */
export function parseDeviceModel(deviceModel?: string | null): { vendor: string; device: string } | null {
  if (!deviceModel) {
    return null
  }

  // Split by underscore to separate vendor from device
  const parts = deviceModel.split('_')

  if (parts.length < 2) {
    // If no underscore, treat entire string as device with unknown vendor
    return {
      vendor: 'Unknown',
      device: deviceModel,
    }
  }

  // First part is vendor, rest is device name
  const vendor = parts[0]
  const device = parts.slice(1).join(' ') // Rejoin with spaces for readability

  return {
    vendor: capitalize(vendor),
    device: capitalize(device),
  }
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Format device attribution for display
 *
 * @param deviceModel - Device model string from database
 * @param source - Activity source (e.g., 'garmin', 'manual', 'strava')
 * @returns Formatted string for display (e.g., "Garmin fenix 7") or null if no device info
 */
export function formatDeviceAttribution(deviceModel?: string | null, source?: string | null): string | null {
  const parsed = parseDeviceModel(deviceModel)

  if (!parsed && !source) {
    return null
  }

  if (!parsed && source) {
    // Only have source, capitalize it
    return capitalize(source)
  }

  if (parsed) {
    return `${parsed.vendor} ${parsed.device}`
  }

  return null
}
